// AsterWallet — Asset registry.
//
// Client-side mirror of the server's ledger.assets table. Every asset
// the app can render carries decimals + display metadata. <Money>
// reads `displayDecimals` from here to know how to render a
// minor-unit bigint into a human string. `decimals` is the storage
// precision and is never rendered directly.
//
// The registry is a static constant for now; Stage II will replace
// this with a `listAssets()` API call and a fetched map. Invariants
// that must survive that swap:
//
//   1. USD is always present. It's the pricing asset and the app's
//      primary quote unit — a missing USD row is a broken app.
//   2. `getAsset(id)` NEVER returns undefined for anything the mock
//      or API ever emits. On miss, a defensive stub returns
//      decimals=2, displayDecimals=2 — mis-rendering is visible; a
//      crash is not.
//   3. `displayDecimals <= decimals` for every asset. Enforcing the
//      other direction would let display precision exceed storage,
//      which is a nonsense state (extra digits would have no source
//      of truth).

import type { Asset } from "./contract";

// USD sits at the top by convention — the pricing anchor.
export const USD: Asset = {
  id: "USD",
  kind: "fiat",
  decimals: 2,
  displayDecimals: 2,
  name: "US Dollar",
  symbol: "$",
};

export const BTC: Asset = {
  id: "BTC",
  kind: "crypto",
  decimals: 8,
  // Full precision. Reconsider as `listDecimals: 5` when a list row
  // proves 8 is too many digits at row scale — don't build that
  // second field until a real row looks wrong.
  displayDecimals: 8,
  name: "Bitcoin",
  symbol: "BTC",
};

export const ETH: Asset = {
  id: "ETH",
  kind: "crypto",
  // 10^18 wei per ETH. `bigint` handles storage comfortably; the
  // human display truncates to 6 digits. `1.000000000000000000 ETH`
  // is unreadable; `1.000000 ETH` is a wallet balance.
  decimals: 18,
  displayDecimals: 6,
  name: "Ethereum",
  symbol: "ETH",
};

export const SOL: Asset = {
  id: "SOL",
  kind: "crypto",
  // 10^9 lamports per SOL.
  decimals: 9,
  displayDecimals: 4,
  name: "Solana",
  symbol: "SOL",
};

export const USDC: Asset = {
  id: "USDC",
  kind: "crypto",
  // 10^6 base units per USDC. Same on Ethereum, Solana, and every
  // chain we care about. It's a stablecoin — 2 display decimals
  // reads like the dollar it tracks.
  decimals: 6,
  displayDecimals: 2,
  name: "USD Coin",
  symbol: "USDC",
};

// Two of choice, per Markets brief. Round out the six.
export const MATIC: Asset = {
  id: "MATIC",
  kind: "crypto",
  decimals: 18,
  displayDecimals: 4,
  name: "Polygon",
  symbol: "MATIC",
};

export const LINK: Asset = {
  id: "LINK",
  kind: "crypto",
  decimals: 18,
  displayDecimals: 4,
  name: "Chainlink",
  symbol: "LINK",
};

export const ASSETS: readonly Asset[] = [USD, BTC, ETH, SOL, USDC, MATIC, LINK] as const;

const REGISTRY: Record<string, Asset> = Object.fromEntries(
  ASSETS.map((a) => [a.id, a]),
);

// Dev-only invariant check. Fires once at module load — visible in the
// Metro console during dev, tree-shaken from release builds.
if (__DEV__) {
  for (const a of ASSETS) {
    if (a.displayDecimals > a.decimals) {
      // eslint-disable-next-line no-console
      console.error(
        `[assets] ${a.id}: displayDecimals (${a.displayDecimals}) > decimals (${a.decimals}) — this is nonsense (display cannot exceed storage precision).`,
      );
    }
  }
}

/**
 * Look up an asset by id. Returns a defensive stub (decimals=2,
 * displayDecimals=2) on miss — see the header comment for why this
 * doesn't throw.
 */
export function getAsset(id: string): Asset {
  const found = REGISTRY[id];
  if (found) return found;
  if (__DEV__) {
    // eslint-disable-next-line no-console
    console.warn(
      `[assets] unknown asset id "${id}" — rendering with USD-shaped fallback (decimals=2). If this is intentional, add the asset to the registry.`,
    );
  }
  return {
    id,
    kind: "fiat",
    decimals: 2,
    displayDecimals: 2,
    name: id,
    symbol: id,
  };
}

/**
 * Shortcut: storage decimals for arithmetic (divisor, padding to
 * full storage precision). <Money> uses this to split a bigint into
 * major/minor with zero information loss, THEN truncates to
 * `displayDecimals` for the visible run.
 */
export function getAssetDecimals(id: string): number {
  return REGISTRY[id]?.decimals ?? 2;
}

/**
 * Divisor used to split a minor-unit bigint into major and minor
 * parts. Cached because bigint exponentiation is not hot but this is
 * called on every render of every Money.
 */
const DIVISOR_CACHE = new Map<number, bigint>();
export function assetDivisor(decimals: number): bigint {
  const cached = DIVISOR_CACHE.get(decimals);
  if (cached !== undefined) return cached;
  const d = 10n ** BigInt(decimals);
  DIVISOR_CACHE.set(decimals, d);
  return d;
}
